import Button from '@ui/buttons/Button'
import ButtonsFixed, { SpaceButtons } from '@ui/buttons/ButtonsFixed'
import Page from '@shell/Page'
import Header from '@ui/elements/Header'
import { useTranslation } from 'react-i18next'
import { View } from 'react-native'

import InstallmentDetails from './installment/InstallmentDetails'
import CancelButton from './prepayment/CancelButton'
import Details from './prepayment/PaymentDetails'
import PaymentProgress from './prepayment/PaymentProgress'
import usePayments, {
  getOrderSetKey,
  PaymentDetails,
} from '@order/hooks/usePayments'
import { router } from 'expo-router'
import { usePaymentDetails } from '@screens/payment/settings/usePaymentDetails'
import usePaymentStatusAmounts from '@order/hooks/usePaymentStatusAmounts'
import { useMemo, useState } from 'react'
import CartUtils from '@order/CartUtils'
import usePaymentStatusPoll from '@order/hooks/usePaymentStatusPoll'
import { usePaymentMethodFee } from '@order/hooks/useInvoiceFee'
import StatusInfo from './StatusInfo'
import ButtonsGrid from '@ui/buttons/ButtonsFixed/ButtonsGrid'
import LogoutTimerButton from './LogoutTimerButton'
import { useModalState } from '@order/hooks/useModalDataState'
import ConfirmationModal from '@ui/components/ConfirmationModal'
import SendPaymentLink from './SendPaymentLink'
import PaymentHelpButton from '@screens/payment/PaymentHelpButton'
import PaymentActionsModal from 'features/payment/modals/PaymentActions/PaymentActionsModal'
import CheckRefundBalanceModal from 'features/payment/modals/CheckRefundBalance/CheckRefundBalanceModal'
import RefundBalanceStatusInfo from './RefundBalanceStatusInfo'
import { NumberUtils } from '@ca/common'
import { useCurrencyDecimals } from 'hooks/useCurrency'

interface Props {
  payment: PaymentDetails
}

export default function PaymentStatusScreen({ payment }: Props) {
  const { t } = useTranslation(['payment', 'status', 'common', 'ui'])
  const text = 'payment:status'

  const [actionsModalOpen, setActionsModalOpen] = useState(false)
  const [checkRefundBalanceOpen, setCheckRefundBalanceOpen] = useState(false)
  const metadata = usePayments((s) => s.payments[payment.localId]?.metadata ?? null)

  const details = usePaymentDetails(payment)
  const paymentDisplayIds = useMemo(() => {
    return details.orders.map((order) => order.name)
  }, [details.orders])
  usePaymentStatusPoll(payment, 5000)

  const amounts = usePaymentStatusAmounts({
    payment,
    values: details.getValues(payment.method),
  })

  const isPayment = payment.method !== 'monthly'
  const isInstallment = payment.method === 'monthly'

  const isFinished = useMemo(
    () => CartUtils.payments.isFinished(payment),
    [payment]
  )

  const { fee } = usePaymentMethodFee(payment.method)

  const finalToPay = fee ? amounts.toPay + fee : amounts.toPay
  const currencyDecimals = useCurrencyDecimals()
  const globemedAttempt = usePayments(
    (s) => s.globemedAttempts[getOrderSetKey(details.orderIds)] ?? null
  )
  const globemedApplied =
    globemedAttempt?.status === 'applied' ? globemedAttempt.amount : null
  // Metadata balance includes GlobeMed, so split it out of the refund row
  const refundBalance = metadata !== null
    ? Math.max(
        0,
        NumberUtils.round.decimal(
          finalToPay - metadata.rest_amount - (globemedApplied ?? 0),
          currencyDecimals
        )
      )
    : null
  const hasUnresolvedGlobemed =
    globemedAttempt?.status === 'uncertain' ||
    globemedAttempt?.status === 'unclear'
  const refundApplied =
    hasUnresolvedGlobemed || (globemedApplied !== null && refundBalance === 0)
      ? null
      : refundBalance

  const confirmation = useModalState()

  const goToCheckout = () => {
    confirmation.close()
    router.navigate('/checkout')
  }

  return (
    <>
      <Page stage="payment" back={false}>
        <Header
          title={t(`${text}.header.title`)}
          desc={t(`${text}.header.desc`)}
        />
        <View className="flex flex-1 flex-col gap-8 px-8">
          {metadata !== null && metadata.rest_amount === 0 ? (
            <RefundBalanceStatusInfo />
          ) : (
            <StatusInfo payment={payment}>
              {!!payment.link && payment.method === 'paylink' && (
                <SendPaymentLink link={payment.link} />
              )}
            </StatusInfo>
          )}
          {isPayment && (
            <>
              <Details
                fee={fee}
                payment={payment}
                amounts={amounts}
                refundApplied={refundApplied}
                globemedApplied={globemedApplied}
                restAmount={metadata?.rest_amount ?? null}
              />
              {!metadata && <PaymentProgress amounts={amounts} />}
              <CancelButton payment={payment} />
            </>
          )}
          {isInstallment && (
            <InstallmentDetails amounts={amounts} payment={payment} />
          )}
          {!isFinished && (
            <PaymentHelpButton onPress={() => setActionsModalOpen(true)} />
          )}
        </View>
        <SpaceButtons />
      </Page>
      <ButtonsFixed>
        <ButtonsGrid>
          <LogoutTimerButton confirmation={!isFinished} />
          <Button
            variant="filled"
            label={t('ui:continue')}
            iconEnd="arrow-narrow-right"
            onPress={!isFinished ? confirmation.open : goToCheckout}
          />
        </ButtonsGrid>
      </ButtonsFixed>
      <ConfirmationModal
        open={confirmation.isOpen}
        onClose={confirmation.close}
        onConfirm={goToCheckout}
        title={t(`${text}.confirm.checkout.title`)}
        desc={t(`${text}.confirm.checkout.desc`)}
      />
      <PaymentActionsModal
        open={actionsModalOpen}
        orderIds={paymentDisplayIds}
        close={() => setActionsModalOpen(false)}
        onCheckRefundBalance={() => setCheckRefundBalanceOpen(true)}
      />
      <CheckRefundBalanceModal
        open={checkRefundBalanceOpen}
        close={() => setCheckRefundBalanceOpen(false)}
        numericOrderIds={details.orderIds}
        localId={payment.localId}
      />
    </>
  )
}
